import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { COUNTRIES, FREQUENT_COUNTRIES, countryName } from '@/lib/countries'

/** The Select needs a value, and an empty string is not one Radix accepts. */
const NONE = 'NONE'

/**
 * The country of an address, as an ISO 3166-1 alpha-2 code.
 *
 * A picker rather than a text field, because the backend stores codes and a typed "Deutschland" is
 * now a refused request. The names are the browser's, in the language the interface is showing.
 *
 * All 249 countries are offered, with the three a stand usually meets pulled to the top. The rest
 * is a long list to scroll, but it is not one somebody has to: the select answers typing with the
 * names on screen, so "Mal" lands on Malta.
 *
 * A code already in the data that the list does not offer is kept as an option of its own, so
 * opening an old record and saving it does not quietly move it to another country.
 */
export function CountrySelect({
  id,
  value,
  onChange,
}: {
  id: string
  value: string | null
  onChange: (value: string | null) => void
}) {
  const { t, i18n } = useTranslation()
  const language = i18n.resolvedLanguage ?? 'de'
  const listed = [...FREQUENT_COUNTRIES, ...COUNTRIES]
  const unlisted = value && !listed.includes(value as (typeof listed)[number]) ? value : null

  const named = (code: string) => `${countryName(code, language)} (${code})`

  // Named and sorted once per language, not once per render. The comparison reads both names, so
  // sorting inside the markup renamed every country in the world on every keystroke in the form
  // this select sits in.
  const rest = useMemo(
    () =>
      [...COUNTRIES]
        .map((code) => ({ code, label: `${countryName(code, language)} (${code})` }))
        .sort((one, other) => one.label.localeCompare(other.label, language)),
    [language],
  )

  return (
    <Select
      value={value ?? NONE}
      onValueChange={(next) => next && onChange(next === NONE ? null : next)}
    >
      <SelectTrigger id={id} className="w-full">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={NONE}>{t('leadForm.countryNone')}</SelectItem>
        <SelectSeparator />
        {FREQUENT_COUNTRIES.map((code) => (
          <SelectItem key={code} value={code}>
            {named(code)}
          </SelectItem>
        ))}
        <SelectSeparator />
        {/* Sorted by what is on screen, not by the code: a list a reader scans has to be in the
            order of the words they are reading. */}
        {rest.map(({ code, label }) => (
          <SelectItem key={code} value={code}>
            {label}
          </SelectItem>
        ))}
        {unlisted && (
          <>
            <SelectSeparator />
            <SelectItem value={unlisted}>{named(unlisted)}</SelectItem>
          </>
        )}
      </SelectContent>
    </Select>
  )
}
